import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useContent } from "@/context/SiteContentContext";

const IMG_DEFAULT = "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1200&q=85";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", username: "", display_name: "" });
  const [busy, setBusy] = useState(false);

  const heroImg = useContent("register.hero.image", IMG_DEFAULT);
  const heroQuote = useContent("register.hero.quote", '"There is room at the table for one more."');
  const overline = useContent("register.overline", "Become a member");
  const title = useContent("register.title", "Reserve your seat at the table.");
  const subtitle = useContent(
    "register.subtitle",
    "Free to join. Open to anyone who loves living beautifully."
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Sanitise iOS autofill artefacts before submit — same trick that fixed
      // the Login "first attempt always fails" bug.
      await register({
        ...form,
        email: form.email.trim().toLowerCase(),
        password: form.password.trim(),
        username: form.username.trim().toLowerCase(),
        display_name: form.display_name.trim(),
      });
      toast.success("Welcome to the salon.");
      navigate("/feed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr 1fr" }} className="auth-grid">
      {/* Back arrow — top-left corner */}
      <Link
        to="/"
        data-testid="register-back"
        aria-label="Back to home"
        style={{
          position: "fixed", top: 20, left: 20, zIndex: 10,
          background: "rgba(250, 249, 245, 0.9)", backdropFilter: "blur(8px)",
          border: "1px solid var(--border-default)",
          width: 40, height: 40, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--text-primary)", textDecoration: "none",
        }}
      >
        <ArrowLeft size={18} strokeWidth={1.6} />
      </Link>
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: "48px 56px" }}>
        <Link to="/" data-testid="register-logo" style={{ marginBottom: 32, display: "inline-flex", alignItems: "center", gap: 14 }}>
          <img src="/brand/le-salon-door.png" alt="" aria-hidden="true" style={{ height: 56, width: 56, display: "block" }} />
          <img src="/brand/le-salon-wordmark.png" alt="Le Salon" style={{ height: 46, display: "block" }} />
        </Link>
        <div style={{ maxWidth: 460 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>{overline}</div>
          <h1 className="editorial-h1" style={{ fontSize: 48, marginTop: 12, marginBottom: 16 }}>
            {title}
          </h1>
          <p className="editorial-body" style={{ marginBottom: 36 }}>
            {subtitle}
          </p>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <label className="overline">Display name</label>
                <input data-testid="register-name-input" required className="input-belle" value={form.display_name} onChange={set("display_name")} placeholder="Amélie L." />
              </div>
              <div>
                <label className="overline">Username</label>
                <input data-testid="register-username-input" required minLength={2} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" className="input-belle" value={form.username} onChange={set("username")} placeholder="amelie" />
              </div>
            </div>
            <div>
              <label className="overline">Email</label>
              <input data-testid="register-email-input" required type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="email" inputMode="email" className="input-belle" value={form.email} onChange={set("email")} placeholder="you@belleinspiration.com" />
            </div>
            <div>
              <label className="overline">Password</label>
              <input data-testid="register-password-input" required minLength={6} type="password" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="new-password" className="input-belle" value={form.password} onChange={set("password")} placeholder="At least 6 characters" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="register-submit-button" style={{ marginTop: 12 }}>
              {busy ? "Creating account…" : "Reserve my seat"}
            </button>
          </form>

          <p style={{ marginTop: 32, fontSize: 14, color: "var(--text-secondary)" }}>
            Already a member?{" "}
            <Link to="/login" data-testid="register-login-link" className="link-underline" style={{ color: "var(--brand-accent)" }}>Sign in →</Link>
          </p>
        </div>
      </div>

      <div className="hide-mobile" style={{ position: "relative", overflow: "hidden" }}>
        <img src={heroImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(28,25,23,0.4) 0%, transparent 50%)" }} />
        <div style={{ position: "absolute", left: 48, top: 48, color: "var(--text-inverse)", maxWidth: 360 }}>
          <div className="overline" style={{ color: "rgba(250,249,245,0.8)" }}>The Salon</div>
          <p className="font-serif" style={{ fontStyle: "italic", fontSize: 30, lineHeight: 1.3, marginTop: 12 }}>
            {heroQuote}
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .auth-grid { grid-template-columns: 1fr !important; }
          .hide-mobile { display: none; }
        }
      `}</style>
    </div>
  );
};

export default Register;
